import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  ActionRow,
  ModalDialog,
  Form,
  Alert,
  Badge,
  Spinner,
} from '@openedx/paragon';
import {
  bulkEnrollCourses, enrollPathway, getUserDetails, registerLearnersWithLegacyLicenses,
} from '../../../data/api';
import LearnerSearchDropdown, { LearnerOption } from './LearnerSearchDropdown';
import { refreshLearnerEnrollments } from '../../../data/thunks';
import { logStudioHomeError } from '../../../logging';
import { getCatalogRemainingAvailability } from './catalogUtils';

interface EnrollmentFormData {
  emailOrUsername: string;
  selectedLicenses: string[];
}

interface ValidationErrors {
  emailOrUsername?: string;
}

interface LicenseOption {
  id: string;
  name: string;
  type: 'legacy_bundle' | 'catalog_course' | 'program';
  available: number | null;
  used: number;
  disabled: boolean;
  course_list?: string[]; // For legacy bundles - contains course IDs
  displayName?: string; // Resolved human-readable course name (catalog_course only)
  courseIds?: string[]; // Course IDs contained in this program (program type only), used for search
}

interface SelectedCompany {
  company_name: string;
  friendly_name: string;
}

interface EnrollLearnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany: SelectedCompany | null;
  licenseOptions: LicenseOption[];
  licenseAllocation: Record<string, number>;
  onLicenseAllocationChange: (allocation: Record<string, number>) => void;
  learners: LearnerOption[];
  companyName?: string;
  rosterIncomplete?: boolean;
}

const EnrollLearnersModal: React.FC<EnrollLearnersModalProps> = ({
  isOpen,
  onClose,
  selectedCompany,
  licenseOptions,
  licenseAllocation,
  onLicenseAllocationChange,
  companyName,
  rosterIncomplete,
  learners,
}) => {
  const dispatch = useDispatch();
  // Form state for current enrollment
  const [currentEnrollment, setCurrentEnrollment] = useState<EnrollmentFormData>({
    emailOrUsername: '',
    selectedLicenses: [],
  });

  // Controlled value for the learner search dropdown display
  const [learnerSearchValue, setLearnerSearchValue] = useState('');

  // License list search
  const [licenseSearchTerm, setLicenseSearchTerm] = useState('');

  // List of enrollments to be submitted
  const [enrollmentsList, setEnrollmentsList] = useState<EnrollmentFormData[]>([]);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>('');
  const [submissionSuccess, setSubmissionSuccess] = useState<string>('');

  // Validate current form
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Email or username
    if (!currentEnrollment.emailOrUsername.trim()) {
      errors.emailOrUsername = 'Email or username is required';
      isValid = false;
    } else {
      // Check for duplicate in enrollments list
      const isDuplicate = enrollmentsList.some(
        enrollment => enrollment.emailOrUsername.toLowerCase() === currentEnrollment.emailOrUsername.toLowerCase(),
      );
      if (isDuplicate) {
        errors.emailOrUsername = 'This email/username has already been added';
        isValid = false;
      }
    }

    // Check if at least one license is selected
    if (currentEnrollment.selectedLicenses.length === 0) {
      errors.emailOrUsername = errors.emailOrUsername || 'Please select at least one course/pathway';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Handle learner selection from dropdown
  const handleLearnerSelect = (learner: LearnerOption) => {
    // Keep the employee roster's canonical username once an existing learner is selected.
    setCurrentEnrollment(prev => ({ ...prev, emailOrUsername: learner.username }));
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.emailOrUsername;
      return newErrors;
    });
  };

  // Handle license selection changes
  const handleLicenseSelectionChange = (licenseId: string, isSelected: boolean) => {
    setCurrentEnrollment(prev => ({
      ...prev,
      selectedLicenses: isSelected
        ? [...prev.selectedLicenses, licenseId]
        : prev.selectedLicenses.filter(id => id !== licenseId),
    }));
  };

  // Add enrollment to list
  const handleAddEnrollment = () => {
    if (!validateForm()) {
      return;
    }

    // Add to list
    setEnrollmentsList(prev => [...prev, { ...currentEnrollment }]);

    // Update license allocation
    const newAllocation = { ...licenseAllocation };
    currentEnrollment.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = (newAllocation[licenseId] || 0) + 1;
    });
    onLicenseAllocationChange(newAllocation);

    // Reset form
    setCurrentEnrollment({
      emailOrUsername: '',
      selectedLicenses: [],
    });
    setLearnerSearchValue('');
    setValidationErrors({});
  };

  // Remove enrollment from list
  const handleRemoveEnrollment = (index: number) => {
    const enrollmentToRemove = enrollmentsList[index];

    // Update license allocation
    const newAllocation = { ...licenseAllocation };
    enrollmentToRemove.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = Math.max(0, (newAllocation[licenseId] || 0) - 1);
    });
    onLicenseAllocationChange(newAllocation);

    // Remove from list
    setEnrollmentsList(prev => prev.filter((_, i) => i !== index));
  };

  // Submit all enrollments
  const handleSubmitAll = async () => {
    if (enrollmentsList.length === 0) {
      setSubmissionError('Please add at least one learner before submitting');
      return;
    }

    if (!selectedCompany) {
      setSubmissionError('No company selected');
      return;
    }

    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const results = {
        successful: 0,
        failed: 0,
        errors: [] as string[],
      };
      const refreshedUsernames: string[] = [];

      // Process each enrollment
      for (const enrollment of enrollmentsList) {
        try {
          // Get user details to retrieve username and Django ID
          let username = enrollment.emailOrUsername;
          let userId: number | null = null;

          try {
            // Enrollment steps are intentionally sequential per learner.
            // eslint-disable-next-line no-await-in-loop
            const userDetails = await getUserDetails(enrollment.emailOrUsername);
            username = userDetails.username || enrollment.emailOrUsername;
            userId = userDetails.id || null;
          } catch (error) {
            logStudioHomeError('cmp_enrollment_user_details_fetch_failed', error);
            results.errors.push(`User not found: ${enrollment.emailOrUsername}`);
            results.failed++;
            // eslint-disable-next-line no-continue
            continue;
          }

          // Separate licenses by type and collect course IDs and legacy bundle names
          const catalogCourses: string[] = [];
          const pathways: string[] = [];
          const legacyBundleNames: string[] = [];

          enrollment.selectedLicenses.forEach(licenseId => {
            const license = licenseOptions.find(l => l.id === licenseId);
            if (license) {
              if (license.type === 'catalog_course') {
                catalogCourses.push(licenseId);
              } else if (license.type === 'program') {
                pathways.push(licenseId);
              } else if (license.type === 'legacy_bundle') {
                // For legacy bundles, collect the bundle name for registration
                legacyBundleNames.push(licenseId);
                // Also add all course IDs from the bundle's course_list for enrollment
                if (license.course_list && license.course_list.length > 0) {
                  catalogCourses.push(...license.course_list);
                }
              }
            }
          });

          // Enroll in catalog courses (includes courses from legacy bundles)
          if (catalogCourses.length > 0) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await bulkEnrollCourses(username, catalogCourses);
            } catch (error) {
              logStudioHomeError('cmp_enrollment_courses_failed', error, {
                courseCount: catalogCourses.length,
              });
              results.errors.push(`Failed to enroll ${username} in courses: ${catalogCourses.join(', ')}`);
            }
          }

          // Register with legacy bundle licenses
          if (legacyBundleNames.length > 0) {
            try {
              // Get user details for legacy bundle registration
              // eslint-disable-next-line no-await-in-loop
              const userProfile = await getUserDetails(enrollment.emailOrUsername);
              // eslint-disable-next-line no-await-in-loop
              await registerLearnersWithLegacyLicenses(selectedCompany.company_name, [
                {
                  first_name: userProfile.name?.split(' ')[0] || 'Unknown',
                  last_name: userProfile.name?.split(' ').slice(1).join(' ') || 'Unknown',
                  email: userProfile.email || enrollment.emailOrUsername,
                  license: legacyBundleNames,
                },
              ]);
            } catch (error) {
              logStudioHomeError('cmp_enrollment_legacy_registration_failed', error, {
                bundleCount: legacyBundleNames.length,
              });
              results.errors.push(`Failed to register ${username} with legacy bundles: ${legacyBundleNames.join(', ')}`);
            }
          }

          // Enroll in pathways
          if (pathways.length > 0 && userId) {
            for (const pathwayId of pathways) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await enrollPathway(userId, pathwayId, selectedCompany.company_name);
              } catch (error) {
                const pathwayName = licenseOptions.find(l => l.id === pathwayId)?.name || pathwayId;
                logStudioHomeError('cmp_enrollment_pathway_failed', error);
                results.errors.push(`Failed to enroll ${username} in pathway: ${pathwayName}`);
              }
            }
          } else if (pathways.length > 0 && !userId) {
            results.errors.push(`Cannot enroll ${username} in pathways: User ID not found`);
          }

          results.successful++;
          refreshedUsernames.push(username);
        } catch (error) {
          logStudioHomeError('cmp_enrollment_processing_failed', error);
          results.failed++;
          results.errors.push(`Failed to process ${enrollment.emailOrUsername}`);
        }
      }

      // Show results
      let successMessage = `Successfully processed ${results.successful} enrollment(s). `;
      if (results.failed > 0) {
        successMessage += `${results.failed} failed. `;
      }
      if (results.errors.length > 0) {
        setSubmissionError(`Errors occurred: ${results.errors.join('; ')}`);
      }

      setSubmissionSuccess(successMessage);

      if (refreshedUsernames.length > 0) {
        await dispatch(refreshLearnerEnrollments(
          selectedCompany.company_name,
          refreshedUsernames,
        ) as any);
      }

      // Clear enrollments list after successful submission
      if (results.failed === 0 && results.errors.length === 0) {
        setEnrollmentsList([]);
        onLicenseAllocationChange({});
      }
    } catch (error) {
      logStudioHomeError('cmp_enrollment_submission_failed', error, {
        enrollmentCount: enrollmentsList.length,
      });
      setSubmissionError('Failed to process enrollments. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form is valid and get validation message
  const formValidation = useMemo(() => {
    const issues: string[] = [];

    if (currentEnrollment.emailOrUsername.trim() === '') {
      issues.push('Select a learner from the dropdown');
    } else if (enrollmentsList.some(
      e => e.emailOrUsername.toLowerCase() === currentEnrollment.emailOrUsername.toLowerCase(),
    )) {
      issues.push('This learner has already been added');
    }

    if (currentEnrollment.selectedLicenses.length === 0) {
      issues.push('Select at least one course/pathway');
    }

    return {
      isValid: issues.length === 0,
      message: issues.length > 0 ? issues[0] : '',
    };
  }, [currentEnrollment, enrollmentsList]);

  // Handle modal close
  const handleClose = () => {
    setEnrollmentsList([]);
    onLicenseAllocationChange({});
    setSubmissionError('');
    setSubmissionSuccess('');
    setCurrentEnrollment({
      emailOrUsername: '',
      selectedLicenses: [],
    });
    setValidationErrors({});
    setLicenseSearchTerm('');
    setLearnerSearchValue('');
    onClose();
  };

  return (
    <ModalDialog
      title="Enroll Existing Learners"
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      hasCloseButton
      isFullscreenOnMobile
    >
      <ModalDialog.Header>
        <ModalDialog.Title>Enroll Existing Learners</ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body>
        {submissionError && (
          <Alert variant="danger" dismissible onClose={() => setSubmissionError('')}>
            {submissionError}
          </Alert>
        )}

        {submissionSuccess && (
          <Alert variant="success" dismissible onClose={() => setSubmissionSuccess('')}>
            {submissionSuccess}
          </Alert>
        )}

        {/* Enrollments List Summary */}
        {enrollmentsList.length > 0 && (
          <Alert variant="info" className="mb-3">
            <strong>{enrollmentsList.length}</strong> learner(s) ready to enroll
            <div className="mt-2">
              {enrollmentsList.map((enrollment) => (
                <Badge key={enrollment.emailOrUsername} variant="secondary" className="mr-2 mb-1">
                  {enrollment.emailOrUsername} ({enrollment.selectedLicenses.length} course{enrollment.selectedLicenses.length !== 1 ? 's' : ''})
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ml-1"
                    onClick={() => handleRemoveEnrollment(index)}
                    style={{ color: 'white', textDecoration: 'none' }}
                  >
                    ✕
                  </Button>
                </Badge>
              ))}
            </div>
          </Alert>
        )}

        <Form>
          <h5 className="mb-3">Learner Information</h5>

          <Form.Group className="mb-3">
            <Form.Label>
              Learner <span className="text-danger">*</span>
            </Form.Label>
            <LearnerSearchDropdown
              learners={learners}
              value={learnerSearchValue}
              onChange={setLearnerSearchValue}
              onSelect={handleLearnerSelect}
              placeholder="Search by name or email..."
              id="enroll-learner-search"
              companyName={companyName}
              rosterIncomplete={rosterIncomplete}
            />
            {validationErrors.emailOrUsername && (
              <div className="text-danger small mt-1">
                {validationErrors.emailOrUsername}
              </div>
            )}
            <Form.Text className="text-muted">
              Search for an existing learner in this organization by name or email.
            </Form.Text>
          </Form.Group>

          {/* License Selection */}
          {licenseOptions.length > 0 && (
            <>
              <h5 className="mb-3 mt-4">Select Courses/Pathways <span className="text-danger">*</span></h5>
              <Form.Text className="mb-2 d-block">
                Select courses or pathways to enroll this learner in. Licenses with 0 available are disabled.
              </Form.Text>
              <Form.Control
                type="text"
                placeholder="Search by name or course ID..."
                value={licenseSearchTerm}
                onChange={(e) => setLicenseSearchTerm(e.target.value)}
                className="mb-2"
              />
              <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {licenseOptions
                  .filter((license) => {
                    if (!licenseSearchTerm.trim()) { return true; }
                    const term = licenseSearchTerm.toLowerCase();
                    if (license.name.toLowerCase().includes(term)) { return true; }
                    // For programs, also search within each contained course ID and display name
                    if (license.courseIds?.some((id) => id.toLowerCase().includes(term))) { return true; }
                    return false;
                  })
                  .map((license) => {
                    const allocated = licenseAllocation[license.id] || 0;
                    let remaining = getCatalogRemainingAvailability(license.available, allocated);
                    if (license.type === 'legacy_bundle' && license.available != null) {
                      remaining = license.available - license.used - allocated;
                    }

                    return (
                      <Form.Check
                        key={license.id}
                        type="checkbox"
                        id={`enroll-license-${license.id}`}
                        label={(
                          <span>
                            {license.name}
                            {' '}
                            <Badge variant={license.disabled ? 'danger' : 'success'}>
                              {typeof remaining === 'number' ? `${remaining} available` : 'Unlimited'}
                            </Badge>
                          </span>
                        )}
                        checked={currentEnrollment.selectedLicenses.includes(license.id)}
                        onChange={(e) => handleLicenseSelectionChange(license.id, e.target.checked)}
                        disabled={license.disabled}
                        className="mb-2"
                      />
                    );
                  })}
                {licenseSearchTerm.trim() && licenseOptions.filter((license) => {
                  const term = licenseSearchTerm.toLowerCase();
                  return license.name.toLowerCase().includes(term)
                    || license.courseIds?.some((id) => id.toLowerCase().includes(term));
                }).length === 0 && (
                  <p className="text-muted mb-0">No licenses match your search.</p>
                )}
              </div>
            </>
          )}

          {licenseOptions.length === 0 && (
            <Alert variant="warning" className="mt-3">
              No courses or pathways available for enrollment. Please add licenses to the company first.
            </Alert>
          )}
        </Form>
      </ModalDialog.Body>

      <ModalDialog.Footer>
        <ActionRow className="justify-content-between w-100">
          <div className="d-flex align-items-center">
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
            {!formValidation.isValid && (
              <small className="text-danger ml-3">{formValidation.message}</small>
            )}
          </div>
          <div className="d-flex">
            <Button
              variant="outline-primary"
              onClick={handleAddEnrollment}
              disabled={!formValidation.isValid || isSubmitting}
              className="mr-2"
            >
              Add Learner
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitAll}
              disabled={enrollmentsList.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Enrolling...
                </>
              ) : (
                `Enroll All (${enrollmentsList.length})`
              )}
            </Button>
          </div>
        </ActionRow>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

export default EnrollLearnersModal;
